import { useState, useMemo } from 'react';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockPartners } from '@/mocks/partners';
import { mockLeads } from '@/mocks/leads';
import { mockOffers } from '@/mocks/offers';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import BaseSelect from '@/components/base/BaseSelect';
import { Download, CheckCircle, Filter, Search } from 'lucide-react';
import Checkbox from '@/components/base/Checkbox';

export default function PayoutsPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    let result = [...mockPayoutLines];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p => {
        const partner = mockPartners.find(pt => pt.id === p.partner_id);
        const lead = mockLeads.find(l => l.id === p.lead_id);
        return partner?.display_name.toLowerCase().includes(q) || lead?.firstname?.toLowerCase().includes(q);
      });
    }
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter);
    }
    return result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [search, statusFilter]);

  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.id)));
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'approved': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'paid': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      case 'pending': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      case 'disputed': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      case 'reversed': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      default: return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const totalSelected = useMemo(() => {
    return filtered.filter(p => selected.has(p.id)).reduce((sum, p) => sum + p.amount_eur, 0);
  }, [selected, filtered]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Payout Lines</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{filtered.length} payout lines</p>
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Selected: €{totalSelected.toLocaleString()}</span>
            <button
              onClick={() => setSelected(new Set())}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl transition-all"
              style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
            >
              <CheckCircle size={14} />
              Approve {selected.size}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md group">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search partner, lead..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl outline-none transition-all"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
          />
        </div>
        <BaseSelect
          value={statusFilter}
          onChange={val => setStatusFilter(val)}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'invoiced', label: 'Invoiced' },
            { value: 'paid', label: 'Paid' },
            { value: 'disputed', label: 'Disputed' },
            { value: 'reversed', label: 'Reversed' },
          ]}
        />
      </div>

      {/* Card Feed — No Table */}
      <div className="rounded-xl border overflow-hidden"
        style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
      >
        {/* Column headers */}
        <div className="px-5 py-3 flex items-center text-[10px] font-medium uppercase tracking-wider border-b"
          style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)', background: 'var(--bg-input)' }}
        >
          <span className="w-8">
            <Checkbox
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleAll}
              indeterminate={selected.size > 0 && selected.size < filtered.length}
            />
          </span>
          <span className="flex-1">Date · Partner</span>
          <span className="w-28 text-right">Amount</span>
          <span className="w-24 text-right">Status</span>
        </div>
        {filtered.map(p => {
          const partner = mockPartners.find(pt => pt.id === p.partner_id);
          const offer = mockOffers.find(o => o.id === p.offer_id);
          const lead = mockLeads.find(l => l.id === p.lead_id);
          const vertical = mockVerticals.find(v => v.id === offer?.vertical_id);
          const geo = mockGeos.find(g => g.id === offer?.geo_id);
          return (
            <div key={p.id} className="px-5 py-4 flex items-center transition-colors hover-surface"
              style={{ borderBottom: '1px solid var(--border-separator)' }}
            >
              <span className="w-8">
                <Checkbox
                  checked={selected.has(p.id)}
                  onChange={() => toggleRow(p.id)}
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>{partner?.display_name}</span>
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>· {lead?.firstname} {lead?.lastname}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{new Date(p.created_at).toLocaleDateString()}</span>
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{vertical?.name} · {geo?.name}</span>
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.trigger_event.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <span className="w-28 text-right text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
                {p.currency} {p.amount_local}
                <span className="text-[11px] ml-1" style={{ color: 'var(--text-muted)' }}>(€{p.amount_eur})</span>
              </span>
              <span className="w-24 text-right">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border" style={statusBadge(p.status)}>
                  {p.status}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}